import { createFileRoute } from '@tanstack/react-router';
import { Building, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { FranchiseFormModal } from '@apps/web/components/admin/franchise_form_modal';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { AdminButton } from '@apps/web/features/admin_design/admin_controls';
import {
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
} from '@apps/web/features/admin_design/admin_shell';
import { AdminTableSkeleton } from '@apps/web/features/admin_design/admin_skeleton';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
} from '@apps/web/features/admin_design/admin_table';
import { toast } from '@apps/web/lib/app_notifications';
import {
    useAdminFranchises,
    useDeleteAdminFranchise,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type Franchise } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/franchises/')({
    head: () => ({
        meta: [
            {
                title: 'Franchises | Phoebe Admin',
            },
        ],
    }),
    component: AdminFranchisesPage,
});

const FRANCHISE_TABLE_COLUMNS = [
    'Name',
    'CNAM',
    'Privacy Policy',
    'Terms of Service',
    'Opt-In Form Page',
    'Actions',
] as const;

function AdminFranchisesPage() {
    const {
        data,
        isLoading,
        error,
        hasRequestFailed,
        mutate: mutateFranchises,
    } = useAdminFranchises();
    const { deleteFranchise } = useDeleteAdminFranchise();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editingFranchise, setEditingFranchise] = useState<Franchise | null>(
        null,
    );
    const [deletingFranchise, setDeletingFranchise] =
        useState<Franchise | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const franchises = data?.franchises ?? [];
    const hasLoadError =
        error !== undefined || (hasRequestFailed && data === undefined);

    // Stable identity per selected franchise so the edit modal does not reset
    // in-progress edits on unrelated re-renders (e.g. SWR revalidation).
    const editInitialData = useMemo(
        () =>
            editingFranchise === null
                ? undefined
                : {
                      name: editingFranchise.name,
                      cnam: editingFranchise.cnam ?? '',
                      privacy_policy_url:
                          editingFranchise.privacy_policy_url ?? '',
                      terms_of_service_url:
                          editingFranchise.terms_of_service_url ?? '',
                      opt_in_form_notion_page_id:
                          editingFranchise.opt_in_form_notion_page_id ?? '',
                  },
        [editingFranchise],
    );

    async function confirmDelete() {
        if (deletingFranchise === null) {
            return;
        }
        setIsDeleting(true);
        try {
            await deleteFranchise(deletingFranchise.id);
            toast.success('Franchise deleted', { persistNotification: false });
            await mutateFranchises();
            setDeletingFranchise(null);
        } catch {
            toast.error('Failed to delete franchise', {
                persistNotification: false,
            });
        } finally {
            setIsDeleting(false);
        }
    }

    return (
        <AdminPage>
            <AdminPageHeader
                title="Franchises"
                meta={`${franchises.length} total`}
                actions={
                    <AdminButton
                        onClick={() => setIsCreateOpen(true)}
                        variant="primary"
                    >
                        <Plus strokeWidth={1.75} />
                        Create Franchise
                    </AdminButton>
                }
            />
            <AdminPageBody>
                {isLoading ? (
                    <AdminTableSkeleton columns={FRANCHISE_TABLE_COLUMNS} />
                ) : hasLoadError ? (
                    <AdminErrorState
                        title="Failed to load franchises"
                        description="Please try again."
                        onRetry={() => {
                            void mutateFranchises();
                        }}
                    />
                ) : franchises.length === 0 ? (
                    <AdminEmptyState
                        icon={Building}
                        title="No franchises yet"
                        description="Create a franchise to group organizations under a shared brand."
                    />
                ) : (
                    <AdminTable>
                        <AdminTableHeader>
                            <tr>
                                {FRANCHISE_TABLE_COLUMNS.map((column) => (
                                    <AdminTableHeadCell
                                        key={column}
                                        className={
                                            column === 'Actions'
                                                ? 'text-right'
                                                : undefined
                                        }
                                    >
                                        {column}
                                    </AdminTableHeadCell>
                                ))}
                            </tr>
                        </AdminTableHeader>
                        <AdminTableBody>
                            {franchises.map((franchise) => (
                                <AdminTableRow key={franchise.id}>
                                    <AdminTableCell className="text-admin-ink font-medium">
                                        {franchise.name}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {franchise.cnam ?? (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell className="max-w-[16rem] truncate">
                                        {franchise.privacy_policy_url ?? (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell className="max-w-[16rem] truncate">
                                        {franchise.terms_of_service_url ?? (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell className="max-w-[16rem] truncate">
                                        {franchise.opt_in_form_notion_page_id ?? (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        <div className="flex justify-end gap-1">
                                            <AdminButton
                                                variant="ghost"
                                                size="iconSm"
                                                onClick={() =>
                                                    setEditingFranchise(
                                                        franchise,
                                                    )
                                                }
                                                aria-label={`Edit ${franchise.name}`}
                                            >
                                                <Pencil strokeWidth={1.75} />
                                            </AdminButton>
                                            <AdminButton
                                                variant="ghost"
                                                size="iconSm"
                                                onClick={() =>
                                                    setDeletingFranchise(
                                                        franchise,
                                                    )
                                                }
                                                aria-label={`Delete ${franchise.name}`}
                                            >
                                                <Trash2 strokeWidth={1.75} />
                                            </AdminButton>
                                        </div>
                                    </AdminTableCell>
                                </AdminTableRow>
                            ))}
                        </AdminTableBody>
                    </AdminTable>
                )}
            </AdminPageBody>
            <FranchiseFormModal
                isOpen={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                mode="create"
            />
            <FranchiseFormModal
                isOpen={editingFranchise !== null}
                onClose={() => setEditingFranchise(null)}
                mode="edit"
                franchiseId={editingFranchise?.id}
                initialData={editInitialData}
            />
            <Dialog
                open={deletingFranchise !== null}
                onOpenChange={(open) => {
                    if (!open && !isDeleting) {
                        setDeletingFranchise(null);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete franchise</DialogTitle>
                        <DialogDescription>
                            Delete {deletingFranchise?.name}? Organizations in
                            this franchise will keep their data but lose the
                            franchise association.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeletingFranchise(null)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Deleting…' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminPage>
    );
}
